import React, { useId, useState } from "react";
import Select, { components as selectComponents } from "react-select";
import cn from "classnames";

import { customStyles as selectStyles } from "../../../constants/select";
import { getSafeErrorText } from "../../../helpers/safeRender";
import styles from "./selectInput.module.css";

// GROWTH-7444: a long option list scrolls, and Chrome and Firefox make a scroller with no focusable children a Tab stop.
// A Tab that react-select lets through (tabSelectsValue off, or the highlighted option already chosen) then lands on the
// list, the input's blur closes the menu, the list unmounts and focus drops to <body>. tabIndex -1 keeps it out of the order.
const MenuList = props => <selectComponents.MenuList {...props} innerProps={{ ...props.innerProps, tabIndex: -1 }} />;

const selectInputComponents = { MenuList };

const SelectInput = ({
  error,
  errorId,
  className,
  classNameError,
  classNameValid,
  classNameInvalid,
  classNameWrap,
  classNamePrefix,
  defaultValue,
  getOptionLabel,
  getOptionValue,
  formatOptionLabel,
  id,
  inputId,
  ariaLabel,
  ariaLabelledBy,
  isInvalid,
  isMulti,
  name,
  noOptionsMessage,
  onChange,
  onBlur,
  options,
  label,
  labelClass,
  placeholder = "Select",
  customStyles,
  value,
  required,
  disabled,
  isAddCustomScrollbar,
  selectRef,
  isNewSelectedColor,
  onInputChange,
  isAutoFocus,
  onMenuOpen,
  onMenuClose,
  isSearchable = true,
  filterOption,
  preventMenuClose = false,
  closeMenuOnSelect = true,
  isClearable = false,
  isUsePortal = false,
  readOnly = false,
  // GROWTH-6586: the menu opens on focus, so with react-select's default a Tab that only wants to move on picks the
  // highlighted option. A filter passes false: Tab then moves focus and chooses nothing.
  tabSelectsValue = true
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const generatedId = useId();
  // A visible label must point at the input; callers that pass no inputId get a generated one. It keeps react-select's own
  // "react-select" prefix, which callers' and tests' selectors match the input by.
  const resolvedInputId = inputId || (label ? `react-select-${generatedId}-input` : undefined);

  /**
   * Forwards react-select's onInputChange unchanged. `actionMeta.action` says why the input changed: "input-change" when the member
   * types; "set-value", "input-blur" and "menu-close" arrive with an empty `inputValue` when react-select resets the box itself.
   *
   * @param {string} inputValue
   * @param {{ action: string, prevInputValue: string }} actionMeta
   */
  const handleChangeInput = (inputValue, actionMeta) => {
    if (onInputChange) {
      onInputChange(inputValue, actionMeta);
    }
  };

  const handleMenuOpen = () => {
    if (readOnly) {
      return;
    }
    setMenuIsOpen(true);
    if (onMenuOpen) {
      onMenuOpen();
    }
  };

  const handleMenuClose = () => {
    if (preventMenuClose) {
      return;
    }
    setMenuIsOpen(false);
    if (onMenuClose) {
      onMenuClose();
    }
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    setMenuIsOpen(false);
    if (onChange) {
      onChange(selectedOption, actionMeta);
    }
  };

  return (
    <div className={classNameWrap}>
      {label && (
        <label className={cn(styles.label, labelClass)} htmlFor={resolvedInputId}>
          {label} {required && <span className={styles.asterisk}>*</span>}
        </label>
      )}
      <Select
        isAddCustomScrollbar={isAddCustomScrollbar}
        classNamePrefix={classNamePrefix}
        className={cn(styles.select, className, { [styles.selectInvalid]: isInvalid }, isInvalid ? classNameInvalid : classNameValid)}
        isDisabled={disabled || readOnly}
        defaultValue={defaultValue}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        formatOptionLabel={formatOptionLabel}
        id={id}
        inputId={resolvedInputId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={errorId && isInvalid ? true : undefined}
        // react-select owns aria-describedby (its live region and placeholder), so the error link is aria-errormessage.
        aria-errormessage={errorId && isInvalid ? errorId : undefined}
        isMulti={isMulti}
        name={name}
        noOptionsMessage={noOptionsMessage}
        onBlur={onBlur}
        onChange={handleSelectChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        menuIsOpen={readOnly ? false : preventMenuClose ? menuIsOpen : undefined}
        closeMenuOnSelect={closeMenuOnSelect}
        options={options}
        placeholder={placeholder}
        styles={customStyles || selectStyles}
        value={value}
        openMenuOnFocus={!readOnly}
        tabSelectsValue={tabSelectsValue}
        components={selectInputComponents}
        ref={selectRef}
        isNewSelectedColor={isNewSelectedColor}
        onInputChange={handleChangeInput}
        autoFocus={isAutoFocus}
        isSearchable={readOnly ? false : isSearchable}
        filterOption={filterOption}
        isClearable={readOnly ? false : isClearable}
        menuPortalTarget={isUsePortal && typeof document !== "undefined" ? document.body : undefined}
        menuPosition={isUsePortal ? "fixed" : "absolute"}
        menuShouldScrollIntoView={!isUsePortal}
      />
      {isInvalid && (
        <div id={errorId} className={cn(styles.error, classNameError)}>
          {getSafeErrorText(error)}
        </div>
      )}
    </div>
  );
};

export default SelectInput;
